/**
 * Member Auth Service
 * Members use the `members` root collection for auth (separate from orgUsers).
 */
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

export const loginMember = async (email, password, expectedOrgId = null) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Primary lookup: by UID (standard for programmatically-created accounts)
  let memberSnap = await getDoc(doc(db, 'members', user.uid));

  // Fallback lookup: by email field (handles manually-created Firestore records
  // where the document ID does not match the Firebase Auth UID)
  if (!memberSnap.exists()) {
    const q = query(collection(db, 'members'), where('email', '==', user.email));
    // Skip records created by the public event-registration form: those people have no portal login.
    const snap = await getDocs(q);
    const candidates = snap.docs.filter(d => !d.data().registrationOnly);
    if (candidates.length) {
      memberSnap = candidates[0];
      // Mirror the doc at members/{auth.uid} so Firestore security rules
      // can locate this member via isOrgUser(orgId) on subsequent requests.
      if (memberSnap.id !== user.uid) {
        setDoc(doc(db, 'members', user.uid), memberSnap.data()).catch(() => {});
      }
    }
  }

  if (!memberSnap.exists()) {
    await signOut(auth);
    throw new Error('Member account not found. Please contact your organisation.');
  }

  const memberData = memberSnap.data();

  if (expectedOrgId && memberData.orgId !== expectedOrgId) {
    await signOut(auth);
    throw new Error(`Access denied. This login is for ${expectedOrgId} members only.`);
  }

  // Load org display name
  let orgName = memberData.orgId;
  const orgDoc = await getDoc(doc(db, 'organizations', memberData.orgId));
  if (orgDoc.exists()) {
    orgName = orgDoc.data().branding?.name || orgName;
  }

  return { user: { ...memberData, orgName, uid: user.uid, email: user.email } };
};

/** Look up the member profile for the currently-signed-in Firebase user.
 *  Tries UID-keyed doc first; falls back to email query for manually-created records. */
const getMemberProfile = async (uid, email) => {
  let snap = await getDoc(doc(db, 'members', uid));
  if (!snap.exists()) {
    const q = query(collection(db, 'members'), where('email', '==', email));
    const result = await getDocs(q);
    const candidates = result.docs.filter(d => !d.data().registrationOnly);
    if (candidates.length) {
      snap = candidates[0];
      if (snap.id !== uid) {
        setDoc(doc(db, 'members', uid), snap.data()).catch(() => {});
      }
    }
  }
  return snap.exists() ? snap : null;
};

export const getCurrentMember = async () => {
  if (!auth.currentUser) return null;
  const snap = await getMemberProfile(auth.currentUser.uid, auth.currentUser.email);
  if (!snap) return null;
  return { ...snap.data(), uid: auth.currentUser.uid, email: auth.currentUser.email };
};

export const isMemberAuthenticated = async () => {
  if (!auth.currentUser) return false;
  const snap = await getMemberProfile(auth.currentUser.uid, auth.currentUser.email);
  return snap !== null;
};

export const logoutMember = async () => {
  await signOut(auth);
  return true;
};

export const resetMemberPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
  return true;
};

export const createMember = async (uid, email, name, orgId, memberProfileId = null) => {
  await setDoc(doc(db, 'members', uid), {
    uid, email, name, orgId, role: 'member',
    memberProfileId, status: 'active',
    createdAt: new Date().toISOString()
  });
};
